// Pure rules for Administration > Organisation > Contacts, kept apart from
// the screen so they can be unit tested without rendering it.

export const CONTACT_TYPES = ["Primary", "Secondary"];

export const CONTACT_TYPE_COLORS = {
  Primary: "#0F766E",
  Secondary: "#2563EB",
};

// Dial codes offered in the phone fields (same set as the login screen).
// `iso` picks the flag icon.
export const DIAL_CODES = [
  { iso: "IN", country: "India", code: "+91" },
  { iso: "SG", country: "Singapore", code: "+65" },
  { iso: "GB", country: "United Kingdom", code: "+44" },
  { iso: "AE", country: "United Arab Emirates", code: "+971" },
  { iso: "US", country: "United States", code: "+1" },
  { iso: "DE", country: "Germany", code: "+49" },
  { iso: "MY", country: "Malaysia", code: "+60" },
  { iso: "AU", country: "Australia", code: "+61" },
];

const DEFAULT_CODE = DIAL_CODES[0].code;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const digitsOf = (value = "") => String(value).replace(/[\s\-()]/g, "");

// "+91 98765 43210" -> { code: "+91", number: "98765 43210" }. A number with
// no known dial code keeps the default code and the text as typed.
export const splitPhone = (phone = "") => {
  const text = String(phone).trim();
  const match = DIAL_CODES.filter((entry) => text.startsWith(entry.code)).sort(
    (a, b) => b.code.length - a.code.length,
  )[0];
  return match
    ? { code: match.code, number: text.slice(match.code.length).trim() }
    : { code: DEFAULT_CODE, number: text };
};

export const joinPhone = (code, number = "") => {
  const text = String(number).trim();
  return text ? code + " " + text : "";
};

// Contacts are stored with full phone strings; the panel edits the dial
// code and the number separately.
export const contactToDraft = (contact) => {
  const phone = splitPhone(contact.phone);
  const whatsapp = splitPhone(contact.whatsapp);
  return {
    id: contact.id,
    name: contact.name,
    designation: contact.designation,
    phoneCode: phone.code,
    phone: phone.number,
    whatsappCode: whatsapp.code,
    whatsapp: whatsapp.number,
    email: contact.email,
    contactType: contact.contactType,
  };
};

export const draftToContact = (draft) => ({
  id: draft.id,
  name: draft.name.trim(),
  designation: draft.designation.trim(),
  phone: joinPhone(draft.phoneCode, draft.phone),
  whatsapp: joinPhone(draft.whatsappCode, draft.whatsapp),
  email: draft.email.trim(),
  contactType: draft.contactType,
});

export const emptyContact = () => ({
  name: "",
  designation: "",
  phoneCode: DEFAULT_CODE,
  phone: "",
  whatsappCode: DEFAULT_CODE,
  whatsapp: "",
  email: "",
  contactType: "Secondary",
});

// Indian mobile numbers are exactly 10 digits; other countries are only
// checked for a plausible length.
const numberError = (code, number) => {
  const digits = digitsOf(number);
  if (!/^\d+$/.test(digits)) return "Enter a valid phone number.";
  if (code === "+91") {
    return digits.length === 10 ? "" : "Enter a 10-digit phone number.";
  }
  return digits.length >= 6 && digits.length <= 12
    ? ""
    : "Enter a valid phone number.";
};

// Returns { field: message } for every invalid field; empty when valid.
// `contacts` is the current list: it is used to reject a duplicate email
// (ignoring the contact being edited, matched by id) and to keep exactly
// one primary contact.
export const validateContact = (draft, contacts = []) => {
  const errors = {};
  const text = (key) => String(draft[key] ?? "").trim();

  if (!text("name")) errors.name = "Name is required.";

  if (!text("phone")) errors.phone = "Phone number is required.";
  else {
    const message = numberError(draft.phoneCode, text("phone"));
    if (message) errors.phone = message;
  }

  // WhatsApp is optional, but must be a real number when given.
  if (text("whatsapp")) {
    const message = numberError(draft.whatsappCode, text("whatsapp"));
    if (message) errors.whatsapp = message.replace("phone", "WhatsApp");
  }

  if (!text("email")) errors.email = "Email address is required.";
  else if (!EMAIL_PATTERN.test(text("email"))) {
    errors.email = "Enter a valid email address.";
  } else if (
    // Existing API records may share an email. Only reject a new or changed email.
    contacts.find((contact) => contact.id === draft.id)?.email.trim().toLowerCase() !== text("email").toLowerCase() &&
    contacts.some(
      (contact) =>
        contact.id !== draft.id &&
        contact.email.trim().toLowerCase() === text("email").toLowerCase(),
    )
  ) {
    errors.email = "A contact with this email address already exists.";
  }

  if (!CONTACT_TYPES.includes(draft.contactType)) {
    errors.contactType = "Select a contact type.";
  } else if (
    draft.contactType === "Secondary" &&
    !contacts.some(
      (contact) => contact.id !== draft.id && contact.contactType === "Primary",
    )
  ) {
    errors.contactType =
      "An organisation needs one primary contact. Mark another contact as Primary first.";
  }

  return errors;
};

// Saves a validated draft into the list. Marking a contact Primary moves
// the others to Secondary, so there is always exactly one.
export const upsertContact = (contacts, draft) => {
  const saved = draftToContact(draft);
  const isNew = saved.id === undefined;
  if (isNew) {
    saved.id = Math.max(0, ...contacts.map((contact) => contact.id)) + 1;
  }
  const others = contacts
    .filter((contact) => contact.id !== saved.id)
    .map((contact) =>
      saved.contactType === "Primary" && contact.contactType === "Primary"
        ? { ...contact, contactType: "Secondary" }
        : contact,
    );
  if (isNew) return [...others, saved];
  return contacts.map((contact) =>
    contact.id === saved.id
      ? saved
      : others.find((other) => other.id === contact.id),
  );
};

// Removing the primary contact hands the role to the first one left.
export const removeContact = (contacts, contactId) => {
  const removed = contacts.find((contact) => contact.id === contactId);
  const remaining = contacts.filter((contact) => contact.id !== contactId);
  if (removed?.contactType === "Primary" && remaining.length) {
    remaining[0] = { ...remaining[0], contactType: "Primary" };
  }
  return remaining;
};
